// Define the subscription plan configuration type
export interface SubscriptionPlan {
  id: string
  headerLabel: string
  title: string
  usersIncluded: number
  description: string
  annualPrice: number
  monthlyPrice: number
  additionalUser?: {
    count: number
    annualPrice: number
    monthlyPrice: number
  }
  notes?: string
  features: string[]
  tier?: string
}

// National Corporation Plan
export const nationalCorporation: SubscriptionPlan = {
  id: "national_corp",
  headerLabel: "B2T",
  title: "National Corporation",
  usersIncluded: 50,
  description: "Membership based on business operations in more than 3 states.",
  annualPrice: 33000,
  monthlyPrice: 3025,
  additionalUser: {
    count: 10,
    annualPrice: 5280,
    monthlyPrice: 484,
  },
  notes: "*Taxes and Fees Included",
  features: [
    "Empower and work with global emerging businesses and suppliers with the capacity and agility to scale.",
    "Curated strategic opportunities in procurement, capital, and mentorship.",
    "Strengthen your supply chain with a more innovative, inclusive, and resilient business ecosystem.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

// Regional Corporation Plan
export const regionalCorporation: SubscriptionPlan = {
  id: "regional_corp",
  headerLabel: "B2T",
  title: "Regional Corporation",
  usersIncluded: 50,
  description:
    "Membership based on business operations within a specific geographic region, 3 states or less.",
  annualPrice: 16500,
  monthlyPrice: 1512.5,
  additionalUser: {
    count: 10,
    annualPrice: 2640,
    monthlyPrice: 242,
  },
  notes: "*Taxes and Fees Included",
  features: [
    "Empower and work with global emerging businesses and suppliers with the capacity and agility to scale.",
    "Curated strategic opportunities in procurement, capital, and mentorship.",
    "Strengthen your supply chain with a more innovative, inclusive, and resilient business ecosystem.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

// Elite Premium Plan
export const elitePremium: SubscriptionPlan = {
  id: "elite_premium",
  headerLabel: "B2T – Business Enterprise",
  title: "Elite Premium",
  usersIncluded: 10,
  description: "Membership based on business revenue above $50M.",
  annualPrice: 3300,
  monthlyPrice: 302.5,
  additionalUser: {
    count: 1,
    annualPrice: 275,
    monthlyPrice: 25.21,
  },
  notes: "*Taxes and Fees Included",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

export const elitPlus: SubscriptionPlan = {
  id: "elite_plus",
  headerLabel: "B2T – Business Enterprise",
  title: "Elite Plus",
  usersIncluded: 10,
  description: "Membership based on business revenue between $10M to $50M",
  annualPrice: 1650,
  monthlyPrice: 151.25,
  additionalUser: {
    count: 1,
    annualPrice: 275,
    monthlyPrice: 25.21,
  },
  notes: "*Taxes and Fees Included",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

export const elite: SubscriptionPlan = {
  id: "elite",
  headerLabel: "B2T – Business Enterprise",
  title: "Elite",
  usersIncluded: 10,
  description: "Membership based on business revenue below $10M.",
  annualPrice: 1100,
  monthlyPrice: 100.83,
  additionalUser: {
    count: 1,
    annualPrice: 165,
    monthlyPrice: 15.13,
  },
  notes: "*Taxes and Fees Included",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

export const solopreneur: SubscriptionPlan = {
  id: "solopreneur",
  headerLabel: "B2T",
  title: "Solopreneur",
  usersIncluded: 1,
  description: "Single-User Membership",
  annualPrice: 438.9,
  monthlyPrice: 43.89,
  notes: "*Taxes and Fees Included",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

export const professional: SubscriptionPlan = {
  id: "professional",
  headerLabel: "B2T",
  title: "Professional",
  usersIncluded: 1,
  description: "Single-User Membership",
  annualPrice: 218.9,
  monthlyPrice: 21.89,
  notes: "*Taxes and Fees Included",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

export const investorsWealthManagementPlatinum: SubscriptionPlan = {
  id: "investors_wealth_management_platinum",
  headerLabel: "Bridge 2 Technologies",
  title: "Investors & Wealth Management",
  usersIncluded: 50,
  description:
    "Membership based on portfolio above $100M in revenue or assets under management (AUM)",
  annualPrice: 16500,
  monthlyPrice: 1512.5,
  additionalUser: {
    count: 10,
    annualPrice: 2640,
    monthlyPrice: 242,
  },
  notes: "*Taxes and Fees Included",
  tier: "Platinum",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

export const investorsWealthManagementGold: SubscriptionPlan = {
  id: "investors_wealth_management_gold",
  headerLabel: "Bridge 2 Technologies",
  title: "Investors & Wealth Management",
  usersIncluded: 50,
  description:
    "Membership based on portfolio between $50M-$100M in revenue or assets under management (AUM)",
  annualPrice: 11000,
  monthlyPrice: 1008.33,
  additionalUser: {
    count: 10,
    annualPrice: 2640,
    monthlyPrice: 242,
  },
  notes: "*Taxes and Fees Included",
  tier: "Gold",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

export const investorsWealthManagementSilver: SubscriptionPlan = {
  id: "investors_wealth_management_silver",
  headerLabel: "Bridge 2 Technologies",
  title: "Investors & Wealth Management",
  usersIncluded: 10,
  description:
    "Membership based on portfolio below $50M in revenue or assets under management (AUM)",
  annualPrice: 3300,
  monthlyPrice: 302.5,
  additionalUser: {
    count: 1,
    annualPrice: 275,
    monthlyPrice: 25.21,
  },
  notes: "*Taxes and Fees Included",
  tier: "Silver",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com.",
  ],
}

export const b2tMember1: SubscriptionPlan = {
  id: "b2t_member_1",
  headerLabel: "Bridge 2 Technologies",
  title: "B2T Member",
  usersIncluded: 50,
  description:
    "Membership based on operations within a specific geographic region.",
  annualPrice: 16500,
  monthlyPrice: 1512.5,
  additionalUser: {
    count: 10,
    annualPrice: 2640,
    monthlyPrice: 242,
  },
  notes: "*Taxes and Fees Included",
  features: [
    "Empower and work with emerging businesses and suppliers with the capacity and agility to scale.",
    "Global business opportunities with precision matching.",
    "Strengthen your supply chain with a more innovative, inclusive, and resilient business ecosystem.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com",
  ],
}

export const b2tMember2: SubscriptionPlan = {
  id: "b2t_member_2",
  headerLabel: "Bridge 2 Technologies",
  title: "B2T Member",
  usersIncluded: 25,
  description:
    "Membership based on operations within a specific geographic region.",
  annualPrice: 11000,
  monthlyPrice: 1008.33,
  additionalUser: {
    count: 5,
    annualPrice: 1320,
    monthlyPrice: 121,
  },
  notes: "*Taxes and Fees Included",
  features: [
    "Empower and work with emerging businesses and suppliers with the capacity and agility to scale.",
    "Global business opportunities with precision matching.",
    "Strengthen your supply chain with a more innovative, inclusive, and resilient business ecosystem.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com",
  ],
}

export const b2tMember3: SubscriptionPlan = {
  id: "b2t_member_3",
  headerLabel: "Bridge 2 Technologies",
  title: "B2T Member",
  usersIncluded: 10,
  description:
    "Membership based on operations within a specific geographic region.",
  annualPrice: 5500,
  monthlyPrice: 504.17,
  additionalUser: {
    count: 1,
    annualPrice: 275,
    monthlyPrice: 25.21,
  },
  notes: "*Taxes and Fees Included",
  features: [
    "Empower and work with emerging businesses and suppliers with the capacity and agility to scale.",
    "Global business opportunities with precision matching.",
    "Strengthen your supply chain with a more innovative, inclusive, and resilient business ecosystem.",
    "Pay your subscription via purchase order by contacting a B2T Ambassador at b2tambassador@theb2t.com",
  ],
}

// Chairman Membership Plan (Admin-only assignment)
export const chairmanMembership: SubscriptionPlan = {
  id: "chairman_membership",
  headerLabel: "B2T",
  title: "Chairman Membership",
  usersIncluded: 1,
  description: "(Admin-assigned only)",
  annualPrice: 0,
  monthlyPrice: 0,
  additionalUser: {
    count: 1,
    annualPrice: 0,
    monthlyPrice: 0,
  },
  notes: "*Admin-assigned plan with zero price",
  features: [
    "Membership to an exclusive platform to work with global companies that spend over $100B+ in procurement annually.",
    "Direct access and real connections to private equity, venture capital, angel investors and family offices.",
    "Immediate opportunities to apply for global procurement projects, capital funding, and professional mentoring.",
  ],
}

// Function to format currency values
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value)
}

// Helper function to get a formatted subscription plan with currency values
export const getFormattedPlan = (plan: SubscriptionPlan) => {
  return {
    ...plan,
    formattedAnnualPrice: formatCurrency(plan.annualPrice),
    formattedMonthlyPrice: formatCurrency(plan.monthlyPrice),
    formattedAdditionalUserAnnualPrice: plan.additionalUser
      ? formatCurrency(plan.additionalUser.annualPrice)
      : null,
    formattedAdditionalUserMonthlyPrice: plan.additionalUser
      ? formatCurrency(plan.additionalUser.monthlyPrice)
      : null,
  }
}

// Function to get a plan by ID
export const getPlanById = (planId: string): SubscriptionPlan => {
  switch (planId) {
    case "national_corp":
      return nationalCorporation
    case "regional_corp":
      return regionalCorporation
    case "elite_premium":
      return elitePremium
    case "elite_plus":
      return elitPlus
    case "elite":
      return elite
    case "solopreneur":
      return solopreneur
    case "professional":
      return professional
    case "investors_wealth_management_platinum":
      return investorsWealthManagementPlatinum
    case "investors_wealth_management_gold":
      return investorsWealthManagementGold
    case "investors_wealth_management_silver":
      return investorsWealthManagementSilver
    case "b2t_member_1":
      return b2tMember1
    case "b2t_member_2":
      return b2tMember2
    case "b2t_member_3":
      return b2tMember3
    case "chairman_membership":
      return chairmanMembership
    // For backward compatibility
    case "investors_wealth_management":
      return investorsWealthManagementPlatinum
    default:
      return nationalCorporation // Default to National Corporation
  }
}
